<script lang="ts">
	import type { DeviceRPi } from "luxedo-data"
	import { Reactivity } from "luxedo-reactivity"
	import SignalStrengthIndicator from "./SignalStrengthIndicator.svelte"
	import NetworkTestInitiator from "../network-test/NetworkTestInitiator.svelte"
	import { Responsive } from "responsive"

	type Props = {
		device: DeviceRPi
	}

	let { device }: Props = $props()

	let isMobile = $derived(Responsive.isMobile)

	$effect.pre(() => {
		Reactivity.useProps(device, ["eidos", "isOnline"])
	})

	let eidos = $derived(Reactivity.propOf(device, "eidos"))
	let isOnline = $derived(Reactivity.propOf(device, "isOnline"))

	let isEthernet = $derived.by(() => {
		if (eidos?.network?.ssid === null) return true
		return false
	})

	let networkName = $derived.by(() => {
		if (eidos?.network?.ssid) return eidos.network.ssid
		else return undefined
	})

	let signalStrength = $derived.by(() => {
		if (isEthernet) return 100
		return eidos?.network?.signal_strength
	})
</script>

<div class="flex-row net-info-row" class:isMobile>
	<div class="net-content">
		{#if isEthernet || networkName}
			<div class="flex-column">
				<span class="label">Connected to: </span>
				<span id="active-network">
					{isEthernet ? "Ethernet" : networkName}
				</span>
			</div>
			<div class="flex-row signal-indicator">
				<SignalStrengthIndicator strength={signalStrength / 100} />
				<div class="flex-column">
					<span class="label">Signal strength:</span>
					<span class="signal-percent">
						{signalStrength ?? 0}%
					</span>
				</div>
			</div>
		{:else}
			<div class="flex-column">
				<span id="active-network" class="disconnected"> Disconnected </span>
			</div>
			<div class="flex-row signal-indicator">
				<SignalStrengthIndicator strength={signalStrength / 100} />
				<div class="flex-column">
					<span class="label">Signal strength:</span>
					<span class="signal-percent">
						{signalStrength ?? 0}%
					</span>
				</div>
			</div>
		{/if}
	</div>

	{#if device.features.includes("NETWORK_TEST") && isOnline}
		<div class="test-button">
			<NetworkTestInitiator />
		</div>
	{/if}
</div>

<style>
	.flex-row {
		align-items: center;
	}

	.label {
		margin: 0 0.75rem 0 0;
		line-height: 1.25em;
		font-size: var(--h3);
		color: var(--color-text);
	}

	.net-content {
		display: flex;
		flex-direction: row;
		align-items: center;
		overflow: hidden;
		width: 100%;
	}

	.signal-percent {
		font-family: var(--font-heading);
		color: var(--color-text-light);
		margin-left: 1rem;
		font-size: var(--text-h3);
		line-height: 1em;
	}

	.signal-indicator {
		margin-left: 1rem;
	}

	.signal-indicator .label {
		margin-left: 1rem;
	}

	#active-network {
		font-size: var(--text-h3);
		line-height: 1em;
		display: -webkit-box;
		-webkit-line-clamp: 2; /* Limit to 2 lines */
		-webkit-box-orient: vertical;
		text-overflow: ellipsis;
		overflow: hidden;
	}

	.test-button {
		margin-left: auto;
	}

	.isMobile #active-network,
	.isMobile .signal-percent {
		font-size: var(--h1);
	}

	.isMobile .net-content {
		justify-content: center;
	}

	.isMobile.net-info-row {
		flex-direction: column;
		justify-content: flex-start;
		gap: 0.5rem;
	}

	.isMobile .test-button {
		font-size: var(--h3);
		margin-left: 0;
	}

	.flex-column {
		overflow: hidden;
	}
</style>
